import * as React from 'react';

export type WriterLayoutMode = 'mobile' | 'tablet' | 'desktop';

const TABLET_MIN = 768; // Tailwind md
const DESKTOP_MIN = 1024; // Tailwind lg

function resolve(width: number): WriterLayoutMode {
  if (width >= DESKTOP_MIN) return 'desktop';
  if (width >= TABLET_MIN) return 'tablet';
  return 'mobile';
}

/**
 * Writer-specific breakpoint hook.
 *
 * The global `useIsMobile` collapses tablet into mobile at 1024px, which is
 * wrong for Writer: an iPad in landscape is a desktop-class surface, while an
 * iPad in portrait behaves like a large phone. We therefore read the three
 * bands directly and let the page mount exactly one shell — the compiled-PDF
 * canvas is expensive, so duplicating it behind `hidden` utilities is not an
 * option.
 *
 * The initial value is resolved synchronously from `window`, so there is no
 * desktop-then-mobile flash on first paint.
 */
export function useWriterLayoutMode(): WriterLayoutMode {
  const [mode, setMode] = React.useState<WriterLayoutMode>(() =>
    typeof window === 'undefined' ? 'desktop' : resolve(window.innerWidth),
  );

  React.useEffect(() => {
    const onChange = () => setMode(resolve(window.innerWidth));
    const tablet = window.matchMedia(`(min-width: ${TABLET_MIN}px)`);
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
    tablet.addEventListener('change', onChange);
    desktop.addEventListener('change', onChange);
    window.addEventListener('orientationchange', onChange);
    onChange();
    return () => {
      tablet.removeEventListener('change', onChange);
      desktop.removeEventListener('change', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  return mode;
}
